import React, {useState} from "react";
import {Checkbox, Col, List, Modal, Row, message} from "antd";

export interface PrintoutColumn {
    key: string;
    label: string;
    width: number;
    group?: string;
    type?: string;
    totalable?: boolean;
    expiration_width?: number;
}

export const DEFAULT_PASSENGER_COLUMNS: PrintoutColumn[] = [
    { key: "lastName",                    label: "LAST NAME",            width: 12, group: "PASSENGER" },
    { key: "firstName",                   label: "FIRST NAME",           width: 12, group: "PASSENGER" },
    { key: "dob",                         label: "DOB",                  width: 17, type: "date" },
    { key: "employer",                    label: "COMPANY",              width: 17 },
    { key: "chargeCode",                  label: "CHARGE CODE",          width: 17 },
    { key: "paxWeight",                   label: "BODY",                 width: 10, type: "number", group: "WEIGHTS", totalable: true },
    { key: "bagWeight",                   label: "BAGS",                 width: 10, type: "number", group: "WEIGHTS", totalable: true },
    { key: "departure",                   label: "FROM",                 width: 17 },
    { key: "destination",                 label: "DESTINATION",          width: 17 },
    { key: "nation",                      label: "COUNTRY OF ORIGIN",    width: 18 },
    { key: "credential:US_DRIVERS_LICENSE", label: "U.S. DRIVERS LICENSE", width: 18, type: "credential", expiration_width: 16 },
    { key: "credential:PASSPORT",         label: "PASSPORT",             width: 18, type: "credential", expiration_width: 16 },
    { key: "credential:PASSPORT_CARD",    label: "PASSPORT CARD",        width: 20, type: "credential", expiration_width: 16 },
    { key: "credential:TWIC",             label: "TWIC",                 width: 20, type: "credential", expiration_width: 16 },
];

export const DEFAULT_CARGO_COLUMNS: PrintoutColumn[] = [
    { key: "name",        label: "NAME",         width: 24 },
    { key: "attentionTo", label: "ATTENTION TO", width: 17 },
    { key: "deliveredBy", label: "DELIVERED BY", width: 17 },
    { key: "weight",      label: "WEIGHT",       width: 10, type: "number", totalable: true },
    { key: "departure",   label: "FROM",         width: 17 },
    { key: "destination", label: "DESTINATION",  width: 17 },
];

const PAX_CORE_COLUMNS = DEFAULT_PASSENGER_COLUMNS.filter(c => c.type !== 'credential');
const PAX_CREDENTIAL_COLUMNS = DEFAULT_PASSENGER_COLUMNS.filter(c => c.type === 'credential');

interface PrintoutModalProps {
    visible: boolean;
    onClose: () => void;
    onDownload: (passengerColumns: PrintoutColumn[], cargoColumns: PrintoutColumn[]) => Promise<void>;
}

const PrintoutModal: React.FC<PrintoutModalProps> = ({ visible, onClose, onDownload }) => {
    const [selectedPaxCols, setSelectedPaxCols] = useState<Set<string>>(
        () => new Set(DEFAULT_PASSENGER_COLUMNS.map(c => c.key))
    );
    const [selectedCgoCols, setSelectedCgoCols] = useState<Set<string>>(
        () => new Set(DEFAULT_CARGO_COLUMNS.map(c => c.key))
    );
    const [loading, setLoading] = useState(false);

    function togglePaxCol(key: string) {
        setSelectedPaxCols(prev => {
            const next = new Set(prev);
            next.has(key) ? next.delete(key) : next.add(key);
            return next;
        });
    }

    function toggleCgoCol(key: string) {
        setSelectedCgoCols(prev => {
            const next = new Set(prev);
            next.has(key) ? next.delete(key) : next.add(key);
            return next;
        });
    }

    // Credential group checkbox derived state
    const selectedCredCount = PAX_CREDENTIAL_COLUMNS.filter(c => selectedPaxCols.has(c.key)).length;
    const allCredsChecked = selectedCredCount === PAX_CREDENTIAL_COLUMNS.length;
    const someCredsChecked = selectedCredCount > 0 && !allCredsChecked;

    function toggleAllCredentials() {
        setSelectedPaxCols(prev => {
            const next = new Set(prev);
            if (allCredsChecked) {
                PAX_CREDENTIAL_COLUMNS.forEach(c => next.delete(c.key));
            } else {
                PAX_CREDENTIAL_COLUMNS.forEach(c => next.add(c.key));
            }
            return next;
        });
    }

    async function handleDownload() {
        const passengerColumns = DEFAULT_PASSENGER_COLUMNS.filter(c => selectedPaxCols.has(c.key));
        const cargoColumns = DEFAULT_CARGO_COLUMNS.filter(c => selectedCgoCols.has(c.key));
        setLoading(true);
        try {
            await onDownload(passengerColumns, cargoColumns);
            onClose();
        } catch (err: any) {
            message.error(`Failed to download printout: ${err?.message || 'Unknown error'}`);
        } finally {
            setLoading(false);
        }
    }

    return (
        <Modal
            title="Generate Printout"
            visible={visible}
            onCancel={onClose}
            okText="Download"
            onOk={handleDownload}
            confirmLoading={loading}
            destroyOnClose
            width={700}
        >
            <Row gutter={[16, 16]}>
                <Col xs={24} md={14}>
                    <List
                        bordered
                        header={<strong>Passenger Columns</strong>}
                        size="small"
                        dataSource={PAX_CORE_COLUMNS}
                        renderItem={col => (
                            <List.Item
                                onClick={() => togglePaxCol(col.key)}
                                style={{ padding: '4px 16px', display: 'flex', justifyContent: 'space-between', cursor: 'pointer' }}
                            >
                                <span>{col.label}</span>
                                <Checkbox checked={selectedPaxCols.has(col.key)} />
                            </List.Item>
                        )}
                    />
                    <List
                        bordered
                        header={
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <strong>Credential Columns</strong>
                                <Checkbox
                                    checked={allCredsChecked}
                                    indeterminate={someCredsChecked}
                                    onChange={toggleAllCredentials}
                                />
                            </div>
                        }
                        size="small"
                        dataSource={PAX_CREDENTIAL_COLUMNS}
                        style={{ marginTop: 8 }}
                        renderItem={col => (
                            <List.Item
                                onClick={() => togglePaxCol(col.key)}
                                style={{ padding: '4px 16px', paddingLeft: 24, display: 'flex', justifyContent: 'space-between', cursor: 'pointer' }}
                            >
                                <span>{col.label}</span>
                                <Checkbox checked={selectedPaxCols.has(col.key)} />
                            </List.Item>
                        )}
                    />
                </Col>
                <Col xs={24} md={10}>
                    <List
                        bordered
                        header={<strong>Cargo Columns</strong>}
                        size="small"
                        dataSource={DEFAULT_CARGO_COLUMNS}
                        renderItem={col => (
                            <List.Item
                                onClick={() => toggleCgoCol(col.key)}
                                style={{ padding: '4px 16px', display: 'flex', justifyContent: 'space-between', cursor: 'pointer' }}
                            >
                                <span>{col.label}</span>
                                <Checkbox checked={selectedCgoCols.has(col.key)} />
                            </List.Item>
                        )}
                    />
                </Col>
            </Row>
        </Modal>
    );
};

export default PrintoutModal;

